/**
 * Servicio de sesión: login, logout, token, verificación.
 * Reemplaza AuthController para el flujo de autenticación.
 */
import { config } from "common/config";
import { CognitoService } from "commonlibs/shared-services/auth/cognito/CognitoService";
import authState from "domain/auth/state/authState";
import { authTokenRepository } from "data-access/repository/auth/AuthTokenRepository";
import { usersApi } from "features/users/api/usersApi";
import { settingsApi } from "features/settings/api/settingsApi";
import useAuthStore from "features/auth/store/authStore";
import AuthErrorCodes from "domain/auth/AuthErrors";
import DomainError from "domain/error/DomainError";
import type { LoginRequest } from "common/models";

const cognitoService = new CognitoService();

const API_KEY_ORQ = "pecop.pideky_escala.near";

export const authSessionService = {
    async login(requestBody: LoginRequest): Promise<void> {
        const migrated = await usersApi.validateMigratedUser(String(requestBody.identifier), API_KEY_ORQ);
        if (!migrated.migrated) {
            throw new DomainError(AuthErrorCodes.USER_NOT_MIGRATED_TO_COGNITO);
        }
        await cognitoService.signIn(requestBody);
    },

    async getAccessToken(): Promise<string | null> {
        const token = await cognitoService.getToken();
        return token ?? null;
    },

    async logout(): Promise<void> {
        await settingsApi.removeSettings();
        await authTokenRepository.removeToken();
        authState.setState({ isLoggedIn: false, inactivityTimeOut: null });
        useAuthStore.getState().logout();
        cognitoService.signOut();
    },

    async verifyInactivityTime(): Promise<void> {
        const lastActivityDate = await settingsApi.getLastActivityDate();
        if (lastActivityDate) {
            const dateDifference = Date.now() - lastActivityDate.getTime();
            if (dateDifference >= config.inactivityTimeout) {
                await authSessionService.logout();
                return;
            }
        }
        await settingsApi.setLastActivityDate();
    },

    async verifySession(): Promise<void> {
        const token = await authSessionService.getAccessToken();
        if (token) {
            authState.setState((s) => ({ ...s, isLoggedIn: true }));
            useAuthStore.getState().setLoggedIn(true);
        } else {
            await settingsApi.removeSettings();
            await authTokenRepository.removeToken();
            authState.setState((s) => ({ ...s, isLoggedIn: false }));
            useAuthStore.getState().setLoggedIn(false);
        }
    },

    setUserIsLoggedIn(): void {
        authState.setState((s) => ({ ...s, isLoggedIn: true }));
        useAuthStore.getState().setLoggedIn(true);
    },

    userIsLoggedIn(): boolean {
        return authState.getState().isLoggedIn;
    },
};
